/**
 * Calculate the available space around the anchor and within its scroll and
 * offset parent.
 */
import getScrollParent from "./getScrollParent"
import { getViewportRect, toClientRect } from "./rect-utils"
import getRelativeRect from "./getRelativeRect"
import getScroll from "./getScroll"
import { curry, pipe } from "ramda"

import getContainer from "./getContainer"
import { getWindow } from "../utils"
import { getClient } from "../useMeasure"

export type Offset = {
  top: number
  left: number
}

const addOffset = curry(
  (offset: Offset, { top, left, height, width }: ClientRect) =>
    toClientRect({
      top: top - offset.top,
      left: left - offset.left,
      height: height + offset.top * 2,
      width: width + offset.left * 2
    })
)

const removeContainerDiff = curry(
  (container: HTMLElement, { top, left, height, width }: ClientRect) => {
    const rect = getClient(container)
    const win = getWindow(rect)
    return toClientRect({
      top: top - rect.top - win.pageYOffset,
      left: left - rect.left - win.pageXOffset,
      height,
      width
    })
  }
)

const getBounds = (
  anchor: HTMLElement,
  element: HTMLElement,
  offset: Offset
) => {
  // Calculate bounds for positioning tether element. Include differences when
  // the container is the document element (HTML tag).
  const anchorParent = anchor.parentElement ?? anchor
  const viewportRect = getViewportRect(anchor)
  const container = getContainer(element, anchorParent)
  const containerRect = getRelativeRect(container)
  const containerScroll = getScroll(container)
  const isContainerHtmlElement =
    container === getWindow(element).document.documentElement
  const bounds = {
    top: isContainerHtmlElement ? containerScroll.scrollTop : 0,
    left: isContainerHtmlElement ? containerScroll.scrollLeft : 0,
    height: Math.min(containerRect.height, viewportRect.height),
    width: Math.min(containerRect.width, viewportRect.width)
  }

  // Calculate anchor position within its offset parent, adding the offset for
  // the tethered element.
  const relativeOffset = pipe(
    getRelativeRect,
    addOffset(offset),
    removeContainerDiff(container)
  )(anchor)

  const topLeft = toClientRect({
    top: bounds.top,
    left: bounds.left,
    height: relativeOffset.top - bounds.top,
    width: relativeOffset.left - bounds.left
  })
  const bottomRight = toClientRect({
    top: topLeft.bottom + relativeOffset.height,
    left: topLeft.right + relativeOffset.width,
    height: bounds.height - topLeft.height - relativeOffset.height,
    width: bounds.width - topLeft.width - relativeOffset.width
  })

  return [topLeft, bottomRight] as [ClientRect, ClientRect]
}

/**
 * Calculates the position of bounds relative to the document
 */
const translateBounds = curry(
  (element: HTMLElement, [topLeft, bottomRight]: [ClientRect, ClientRect]) => {
    const scrollParent = getScrollParent(element)
    const scroll = getScroll(scrollParent)

    return [
      toClientRect({
        top: topLeft.top - scroll.scrollTop + window.pageYOffset,
        left: topLeft.left - scroll.scrollLeft + window.pageXOffset,
        height: topLeft.height,
        width: topLeft.width
      }),
      toClientRect({
        top: bottomRight.top - scroll.scrollTop + window.pageYOffset,
        left: bottomRight.left - scroll.scrollLeft + window.pageXOffset,
        height: bottomRight.height,
        width: bottomRight.width
      })
    ]
  }
)

const getBoundingOffsets = (
  anchor: HTMLElement,
  element: HTMLElement,
  offset: Offset
) => {
  const [topLeft, bottomRight] = translateBounds(
    anchor.parentElement ?? anchor,
    getBounds(anchor, element, offset)
  )

  return {
    top: toClientRect({
      top: topLeft.top,
      left: topLeft.left,
      height: topLeft.height,
      width: bottomRight.right - topLeft.left
    }),
    right: toClientRect({
      top: topLeft.top,
      left: bottomRight.left,
      height: bottomRight.bottom - topLeft.top,
      width: bottomRight.width
    }),
    bottom: toClientRect({
      top: bottomRight.top,
      left: topLeft.left,
      height: bottomRight.height,
      width: bottomRight.right - topLeft.left
    }),
    left: toClientRect({
      top: topLeft.top,
      left: topLeft.left,
      height: bottomRight.bottom - topLeft.top,
      width: topLeft.width
    })
  }
}

export default getBoundingOffsets
